import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  AccountFilterParams,
  AccountRecord,
  AccountSummaryCounts,
  SaveAccountRequest,
} from '../types/accounting';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const mapAccount = (value: Record<string, unknown>): AccountRecord => ({
  id: String(value.id || ''),
  company_id: Number(value.company_id || 0),
  code: String(value.code || ''),
  name: String(value.name || ''),
  type: value.type as AccountRecord['type'],
  subtype: (value.subtype as string | null) ?? null,
  parent_account_id: (value.parent_account_id as string | null) ?? null,
  normal_balance: value.normal_balance as AccountRecord['normal_balance'],
  is_system: Boolean(value.is_system),
  is_active: Boolean(value.is_active),
  allows_manual_entries: Boolean(value.allows_manual_entries),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const applyAccountFilters = <T>(
  query: T,
  filters: {
    search?: string;
    type?: AccountFilterParams['type'];
  },
) => {
  let nextQuery = query as any;
  const trimmedSearch = filters.search?.trim();

  if (filters.type && filters.type !== 'all') {
    nextQuery = nextQuery.eq('type', filters.type);
  }

  if (trimmedSearch) {
    nextQuery = nextQuery.or(
      `code.ilike.%${trimmedSearch}%,name.ilike.%${trimmedSearch}%,subtype.ilike.%${trimmedSearch}%`,
    );
  }

  return nextQuery as T;
};

export const fetchAccounts = async ({
  companyId,
  search,
  type = 'all',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: AccountFilterParams): Promise<
  ApiResponse<PaginatedResponse<AccountRecord>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('accounts')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('code', { ascending: true })
      .range(from, to);

    query = applyAccountFilters(query, { search, type });

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) => mapAccount(item)),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch accounts',
      data: undefined,
    };
  }
};

export const fetchAccountSummary = async (
  companyId: number,
  search?: string,
): Promise<ApiResponse<AccountSummaryCounts>> => {
  try {
    const baseQuery = (type?: AccountRecord['type']) => {
      let query = supabase
        .from('accounts')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);

      query = applyAccountFilters(query, { search, type });
      return query;
    };

    const [totalResult, assetResult, liabilityResult, revenueResult] =
      await Promise.all([
        baseQuery(),
        baseQuery('asset'),
        baseQuery('liability'),
        baseQuery('revenue'),
      ]);

    const errors = [
      totalResult.error,
      assetResult.error,
      liabilityResult.error,
      revenueResult.error,
    ].filter(Boolean);

    if (errors.length) {
      return { success: false, error: errors[0]?.message, data: undefined };
    }

    return {
      success: true,
      data: {
        totalAccounts: totalResult.count || 0,
        assetAccounts: assetResult.count || 0,
        liabilityAccounts: liabilityResult.count || 0,
        revenueAccounts: revenueResult.count || 0,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch account summary',
      data: undefined,
    };
  }
};

export const fetchAccountOptions = async (
  companyId: number,
): Promise<ApiResponse<AccountRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('company_id', companyId)
      .order('code', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapAccount(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch account list',
      data: [],
    };
  }
};

export const fetchAccountById = async (
  companyId: number,
  accountId: string,
): Promise<ApiResponse<AccountRecord | null>> => {
  try {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('company_id', companyId)
      .eq('id', accountId)
      .maybeSingle();

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return {
      success: true,
      data: data ? mapAccount(data) : null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch account',
      data: null,
    };
  }
};

export const saveAccount = async (
  input: SaveAccountRequest,
): Promise<ApiResponse<AccountRecord>> => {
  const payload = {
    company_id: input.companyId,
    code: input.code.trim(),
    name: input.name.trim(),
    type: input.type,
    subtype: input.subtype?.trim() || null,
    parent_account_id: input.parentAccountId ?? null,
    normal_balance: input.normalBalance,
    is_active: input.isActive ?? true,
    allows_manual_entries: input.allowsManualEntries ?? true,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('accounts')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapAccount(data) };
    }

    const { data, error } = await supabase
      .from('accounts')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapAccount(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save account',
      data: undefined,
    };
  }
};

export const deleteAccount = async (
  companyId: number,
  accountId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('accounts')
      .delete()
      .eq('company_id', companyId)
      .eq('id', accountId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to delete account',
      data: null,
    };
  }
};
